import gateway from '../gateway/gateway'
import { Guest, GuestsOrAttendees, Recipient } from '../types/loft'
import { Person } from '../types/person'
import { User } from '../types/user'

export const findPeopleWithoutDuplicates = async (guests: GuestsOrAttendees, auth: User) => {
  if (guests.length === 0) {
    return []
  }

  const query = {
    email_addresses: guests.map(({ emailAddress }) => emailAddress),
    sort_by: 'last_contacted_at',
  }

  const people = await gateway.publicPeople.get(query)

  const attendees = guests.map(({ emailAddress, displayName, ...rest }) => {
    const person = people.find(
      (person: Person) => person.email_address.toLowerCase() === emailAddress.toLowerCase()
    )
    return {
      ...rest,
      ...person,
      displayName: person?.display_name || displayName,
      emailAddress,
    }
  })

  if (!auth.account.permission_names.includes('create_people_when_lofting')) {
    return attendees.filter(({ id }) => id)
  }

  return attendees
}

const EMAIL_REGEXP = /^\s*([-a-z0-9+.\!\$\%\&\'\*\+\-\/\=\?\^\_\{\}\~]{1,64})@((?:[-a-z0-9]+\.)+[a-z]{2,})\s*$/i

export const isEmailValid = (email: string) => {
  if (typeof email !== 'string') {
    return false
  }

  return EMAIL_REGEXP.test(email)
}

const isEventOrganizer = ({
  email,
  guests = [],
}: {
  email: string | null
  guests: GuestsOrAttendees
}) => guests.some(guest => guest?.emailAddress?.toLowerCase() === email?.toLowerCase())

const isEmailDomainExcluded = ({
  email,
  excludedDomains = [],
}: {
  email: string | null
  excludedDomains: string[]
}) => {
  const domainRegex = /.*@/
  const domain = email?.replace(domainRegex, '') || ''

  return excludedDomains.includes(domain)
}

export const filterPotentialGuests = (
  loggedInUserEmail: string | null,
  guests: GuestsOrAttendees = [],
  excludedDomains: string[] = []
) => {
  const isUserEventOrganizer = isEventOrganizer({
    email: loggedInUserEmail,
    guests,
  })

  const finalGuests = isUserEventOrganizer
    ? guests.filter(guest => guest.emailAddress.toLowerCase() !== loggedInUserEmail?.toLowerCase())
    : guests

  return finalGuests.filter(
    (guest: Guest | Recipient) =>
      guest.emailAddress.toLowerCase() !== loggedInUserEmail?.toLowerCase() &&
      isEmailValid(guest.emailAddress) &&
      !isEmailDomainExcluded({ email: guest.emailAddress, excludedDomains })
  )
}

export const isMainGuestInAttendeesList = ({
  mainGuest,
  guests,
}: {
  mainGuest: Guest | Recipient | null
  guests: GuestsOrAttendees | null
}) =>
  Boolean(
    guests?.find(
      guest => guest.emailAddress.toLowerCase() === mainGuest?.emailAddress.toLowerCase()
    )
  )

export const findValidMainGuest = (
  loggedInUserEmail: string | null,
  guests: GuestsOrAttendees = [],
  excludedDomains: string[] = []
) => filterPotentialGuests(loggedInUserEmail, guests, excludedDomains)[0] || null

export const getDefaultPerson = ({ displayName, emailAddress }: Recipient) => ({
  id: null,
  display_name: displayName,
  first_name: displayName,
  last_name: displayName,
  email_address: emailAddress,
  loaded: true,
})

export const resolveRecipientName = (recipient?: Guest | Recipient) => {
  if (!recipient || recipient.displayName === recipient.emailAddress) {
    return ''
  }

  return recipient.displayName
}

export const getAccountId = (mainGuest?: Guest | Recipient) =>
  mainGuest && 'account' in mainGuest ? mainGuest.account?.id : null

export const getCadenceId = (mainGuest?: Guest | Recipient) =>
  mainGuest && 'last_completed_step_cadence' in mainGuest
    ? mainGuest.last_completed_step_cadence?.id
    : null

export const getStepId = (mainGuest?: Guest | Recipient) =>
  mainGuest && 'last_completed_step' in mainGuest ? mainGuest.last_completed_step?.id : null

export const getMainGuest = (mainGuest: Guest | Recipient) => ({
  emailAddress: mainGuest?.emailAddress,
  last_completed_step: 'last_completed_step' in mainGuest ? mainGuest.last_completed_step : {},
  last_completed_step_cadence:
    'last_completed_step_cadence' in mainGuest ? mainGuest.last_completed_step_cadence : {},
  account: 'account' in mainGuest ? mainGuest.account?.id : {},
  id: 'id' in mainGuest ? mainGuest?.id : null,
  do_not_contact: 'do_not_contact' in mainGuest ? mainGuest.do_not_contact : false,
  phone: 'phone' in mainGuest ? mainGuest.phone : '',
  person_company_name: 'person_company_name' in mainGuest ? mainGuest.person_company_name : null,
  displayName: mainGuest.displayName,
})
